export interface User {
  name: string;
  email: string;
  account: any;
  isExternal?: boolean;
  token?: string;
}

export interface ReviewerRef {
  _id: string;
  name: string;
  email: string;
}

export interface ReviewerAssessment {
  _id?: string;
  reviewer: ReviewerRef;
  // Deprecated: reviewers no longer set a decision — only the decision maker does
  decision?: 'approve' | 'flag' | 'reject' | null;
  // Aligned with comment Severity values so the UI uses one vocabulary
  riskLevel: 'critical' | 'major' | 'minor' | 'suggestion' | 'none' | null;
  summary: string;
  submittedAt?: string;
  // 2026-08-31 — present only on a decision maker's stand-in review.
  // `reviewer` stays the AUTHOR; this says which step it stands in for and
  // whose it was. Absent on every ordinary assessment.
  standsInFor?: {
    step: 'medical' | 'legal';
    reviewer?: { _id: string | null; name: string | null; email: string | null };
  };
}

export interface ContentItem {
  name: string;
  type?: string;       // Purpose LABEL (Presentation, Video, etc.)
  audience?: string;   // Audience LABEL (HCP, Clinical partner, etc.)
  // v2 (2026-08-14, D90) — slug-stored lineage dimensions. Resolve to
  // labels for display via the taxonomy registry.
  topic?: string | null;      // e.g. 'science', 'osteoarthritis'
  geography?: string | null;  // 'global' | 'sea' | 'emea' | 'americas'
  version?: string;    // Version (V1, V2.1)
  date?: string;       // Date (YYYYMMDD)
  sharePointPath?: string;
  gitBranch?: string;
  fileId?: string;
  // PR #12 (8C smoke) — embedded media child cycles reference an
  // extracted binary stored locally under backend/uploads/embedded-media/.
  localFilePath?: string;
  mimeType?: string;
  // 2026-06-12 — byte-identity captured at submit, sourced from Microsoft
  // Graph. Used for dedup detection + integrity audit. Algo is whichever
  // SharePoint surfaced ('sha1' / 'sha256' / 'quickXor').
  contentHash?: string | null;
  contentHashAlgo?: string | null;
  // 14e-A editable-source capture (D81).
  sourceFileId?: string | null;
  sourceFileName?: string | null;
  sourceLink?: string | null;
  noEditableSourceReason?: string | null;
}

export interface FileValidation {
  valid: boolean;
  errors: string[];
  validatedAt?: string;
}

export type PreScreenStatus = 'pending' | 'needs_mcl' | 'fast_track' | null;
export type PostApprovalAction = 'content_hub' | 'website_page' | 'portal_content' | 'manual' | null;

export type ContentType = 'docx' | 'pptx' | 'pdf' | 'image' | 'video' | 'webpage' | null;

export interface ParsedSection {
  sectionId: string;
  type: string;
  content: string;
  metadata?: Record<string, any>;
}

export interface WebPageState {
  stateId: string;          // 'default', 'tab-0-1', 'carousel-0-1', ...
  label: string;            // 'Default view', 'Tab: Features', 'Slide 2'
  screenshotPath: string;
  sections: ParsedSection[];
}

export interface ChildReview {
  _id: string;
  contentItem: ContentItem;
  contentType: ContentType;
  status: CycleStatus;
  // Declared 2026-08-19 for the per-tab reviewed markers, on the assumption
  // that GET /parsed-content "returns whole child documents". It does not —
  // it projects four fields, and reviewedBy was not one of them, so the
  // marker read undefined and said "not reviewed" forever. The route now
  // returns it explicitly (routes/contentReview.js, children.map). Optional
  // here only because older responses omit it; TypeScript cannot check this
  // boundary, so the route and this type must be changed together.
  reviewedBy?: { reviewer: { _id: string; name: string; email: string }; reviewedAt?: string }[];
}

export interface ReviewCycle {
  _id: string;
  contentItem: ContentItem;
  submissionNotes?: string;
  // Phase 8E — 'canceled' is an admin-only terminal state used to abandon a
  // mid-flight cycle and unblock the lineage so a fresh submit can proceed.
  status: 'pending_prescreen' | 'pending_review' | 'in_review' | 'approved' | 'revision_needed' | 'rejected' | 'canceled' | 'superseded';
  preScreenStatus: PreScreenStatus;
  preScreenedBy?: ReviewerRef;
  preScreenedAt?: string;
  fileValidation?: FileValidation;
  assignedReviewers: ReviewerRef[];
  // DM routing (2026-09-02) — who this cycle's DM-facing notifications go
  // to. Routing only, never access. Empty/absent = default DMs → all DMs.
  assignedDecisionMakers?: ReviewerRef[];
  allReviewersComplete: boolean;
  postApprovalAction: PostApprovalAction;
  parentReview?: string;
  contentType: ContentType;
  // Phase 11 — webpage cycles can be either 'snapshot' (single self-contained
  // HTML rendered in a sandboxed iframe) or 'multistate' (legacy tab/carousel
  // screenshot pipeline). null for non-webpage cycles.
  contentSubtype?: 'snapshot' | 'multistate' | null;
  snapshotPath?: string;
  snapshotStatus?: 'pending' | 'ready' | 'failed' | null;
  sourceUrl: string;
  // Phase 8C — environment tag for web page cycles. Sandbox is the expected
  // case; 'production' surfaces a badge in the queue. Null on file cycles.
  sourceEnv?: 'sandbox' | 'production' | null;
  // Phase 8C — lineage link to the previous cycle in the same content
  // thread (Purpose + Audience + Title). Auto-set at submit.
  previousVersion?: string | null;
  // Spec §11.4 / Phase 5 — set on a SUCCESSOR created via POST /cycles/:id/revisions.
  revisionOf?: string | null;
  supersededBy?: string | null;
  // Spec §9 / Phase 4b–6 — amendment channel record written at decision time.
  amendment?: {
    channel: string; label: string; fallback: boolean; reason?: string; executed: boolean; itemCount: number; generatedAt?: string;
    appliedCopy?: {
      fileId: string | null; webUrl: string | null; fileName: string | null;
      applied: string[]; annotated: string[]; unanchored: Array<{ id: string; reason: string }>;
      skipped: boolean; reason: string; at?: string;
      // 2026-08-24 (Q1) — the amender now writes OVER the original and keeps a
      // tracked-changes rendering beside it. `wroteOriginal` is what tells the
      // UI whether a file was actually touched, which decides whether the
      // remaining-work count is "what was left as comments" or "all of it".
      wroteOriginal?: boolean;
      postApplyHash?: string | null;
      auditCopy?: { fileId?: string | null; webUrl?: string | null; fileName?: string; skipped: boolean; reason: string } | null;
    } | null;
  } | null;
  // Q3 (2026-08-24) — approved, but held until the agreed fixes are confirmed.
  // A flag rather than a status, so every TERMINAL_STATUSES check keeps
  // working unchanged; only the code that gates shipping knows about it.
  pendingFixes?: boolean;
  fixesConfirmation?: {
    confirmedBy?: { teamMemberId?: string; name?: string; email?: string };
    at?: string | null;
    attestation?: boolean;
    remainingAtConfirm?: number;
  } | null;
  supersededAt?: string | null;
  addresses?: Array<{ commentId: string; at?: string }>;
  reentry?: 'review' | 'dm' | null;
  reentryDetail?: string;
  screenshotPath: string;
  screenshots?: WebPageState[];
  parsedContent: ParsedSection[];
  reviewers: ReviewerRef[];
  // Phase 11.2 — lightweight per-reviewer "I've seen this" acknowledgment,
  // primarily used on child cycles (embedded videos). Auto-populated when a
  // reviewer comments. Empty on legacy cycles — fallback gate uses comments.
  reviewedBy?: Array<{ reviewer: ReviewerRef; reviewedAt: string }>;
  reviewerAssessments: ReviewerAssessment[];
  // 2026-08-24 — the producer's commercial read-back to the decision maker.
  // Not a risk judgement and deliberately outside reviewerAssessments (that
  // array is rebuilt from assignedReviewers on every reviewer change).
  producerReadback?: {
    text?: string;
    submittedBy?: { teamMemberId?: string | null; name?: string | null; email?: string | null };
    submittedAt?: string | null;
  } | null;
  aiPreReview: string;
  decisionMakerNotes: string;
  finalDecision: 'approved' | 'revised' | 'rejected' | null;
  // WS1.2 — decision audit on the cycle (walk finding #8).
  decidedBy?: { teamMemberId?: string | null; name?: string | null; email?: string | null };
  decidedAt?: string | null;
  // WS2 (D75) — the Channel label picked at submission.
  channel?: string | null;
  // WS3 — per-step post-approval automation trail.
  automation?: Array<{
    step: string;
    status: 'ok' | 'failed' | 'skipped';
    detail?: string;
    error?: string;
    at?: string;
  }>;
  // O-2 — backgrounded pipeline re-run marker (the UI polls completedAt).
  automationRerun?: {
    startedAt?: string | null;
    completedAt?: string | null;
    startedBy?: { teamMemberId?: string | null; name?: string | null };
    error?: string | null;
  } | null;
  childCount?: number;
  // Phase 6B — per-cycle review deadline (ISO date string when set)
  reviewDeadline?: string | null;
  deadlineSetBy?: {
    _id?: string;
    name?: string;
    email?: string;
    role?: 'producer' | 'decision_maker' | 'admin';
    at?: string;
  };
  deadlineRemindersSent?: { day1?: boolean; missed?: boolean };
  // Phase 8E — admin-cancellation audit. Populated only when status='canceled'.
  canceledBy?: { teamMemberId?: string; name?: string; email?: string } | null;
  canceledAt?: string | null;
  cancellationReason?: string;
  // Phase 8D — producer attribution (denormalized).
  producer?: { teamMemberId?: string; name?: string; email?: string } | null;
  // Phase 12A — sequenced review + edit lock + audit trail (D64-D67, D69).
  reviewSequence?: Array<{ step: 'medical' | 'legal' | 'producer'; order: number }>;
  stepCompletions?: Array<{
    step: 'medical' | 'legal' | 'producer';
    completedBy?: { teamMemberId?: string | null; name?: string | null };
    completedAt?: string;
  }>;
  editLock?: {
    holder?: { teamMemberId?: string | null; name?: string | null };
    step?: 'medical' | 'legal' | 'producer' | null;
    since?: string | null;
    expiresAt?: string | null;
    autoReleaseReason?: 'stepComplete' | 'sessionTimeout' | 'multiDayIdle' | 'dmForceRelease' | null;
  };
  editHistory?: Array<{
    teamMemberId: string;
    at: string;
    step: 'medical' | 'legal' | 'producer';
    byteHashBefore?: string | null;
    byteHashAfter?: string | null;
  }>;
  resetSteps?: Array<'medical' | 'legal' | 'producer'>;
  createdAt: string;
  updatedAt: string;
}

export type SectionType = 'text' | 'slide' | 'page' | 'image' | 'timestamp' | 'dom_section' | '';

export type Disposition = 'undecided' | 'apply' | 'wont_apply' | 'already_applied';
export const DISPOSITION_LABELS: Record<Disposition, string> = {
  undecided: 'Undecided',
  apply: 'Apply',
  wont_apply: "Won't apply",
  already_applied: 'Already applied'
};
/** Absent === undecided (rows predating the field). */
export const isUndecided = (c: { disposition?: Disposition | null }) => !c.disposition || c.disposition === 'undecided';
/** A finding root — the only thing that carries a disposition. */
export const carriesDisposition = (c: { kind?: string; parentComment?: string | null }) => c.kind !== 'consultation' && !c.parentComment;

/** Spec §9 / Phase 4b — GET /cycles/:id/change-spec. */
export interface ChangeSpec {
  channel: 'in_file_annotation' | 'companion_annotation' | 'annotated_copy' | 'task_handoff' | 'cms_change_list' | 'comment_sheet';
  label: string;
  fallback: boolean;
  reason: string;
  executed: boolean;
  contentType: string | null;
  contentName: string;
  itemCount: number;
  apply: ChangeSpecItem[];
  alreadyApplied: ChangeSpecItem[];
  wontApply: ChangeSpecItem[];
  undecided: ChangeSpecItem[];
  markdown: string;
  /** HTML twin of `markdown` with thumbnails inline — the clipboard's text/html flavor. */
  html?: string;
}
export interface ChangeSpecItem {
  commentId: string;
  severity: ReviewComment['severity'];
  disposition: Disposition;
  reviewer: string | null;
  source: string | null;
  anchor: { kind: string; label: string; [k: string]: unknown };
  issue: string;
  suggestedFix: string;
  /** Layout comments: JPEG data-URL crop of the marked region. */
  anchorThumbnail?: string;
  reason: string;
}

export interface ReviewComment {
  _id: string;
  reviewCycle: string;
  reviewer: ReviewerRef;
  issue: string;
  // Phase 12F — resolved @mentions persisted by the backend mention
  // resolver. Used by renderWithMentions (WS1.7) to pill-highlight.
  mentionedTeamMembers?: Array<{
    teamMemberId?: string | null;
    name?: string | null;
    email?: string | null;
    notified?: boolean;
  }>;
  severity: 'critical' | 'major' | 'minor' | 'suggestion' | 'none';
  suggestedFix: string;
  location: string;
  sectionId: string;
  sectionType: SectionType;
  sectionContent: string;
  textSelection?: string;
  selectionStart?: number;
  selectionEnd?: number;
  coordinates?: {
    x: number; y: number; width?: number; height?: number;
    /** Slide inside a presentation modal — marker shows only while that slide is up. */
    slide?: { widget: string; index: number; label?: string };
  };
  /** Layout-view comments: small JPEG data-URL crop of the marked region. */
  anchorThumbnail?: string;
  timestamp?: number;
  stateId?: string;
  stateLabel?: string;
  resolved: boolean;
  // Spec §8 (2026-08-20) — finding (severity, disposition, DM closes) vs
  // consultation (a question; closed by being answered). Chosen by the
  // author in CommentComposer, never inferred. Optional because rows
  // written before the field are findings (backfilled server-side).
  kind?: 'finding' | 'consultation';
  // Spec §2.2 / Phase 4 (2026-08-20) — the DM's decision on a finding,
  // separate from `resolved`. Absent on rows predating the field: treat
  // absent as 'undecided' everywhere (never compare === 'undecided' alone).
  disposition?: Disposition;
  // Spec §3.1 / Phase 6 — literal replacement for textSelection; system-applied flag.
  proposedText?: string;
  appliedBySystem?: boolean;
  systemAppliedAt?: string | null;
  dispositionReason?: string;
  dispositionBy?: { teamMemberId?: string | null; name?: string | null; email?: string | null };
  dispositionAt?: string | null;
  // Phase 12F threads / COMMENT_DISPOSITION_SPEC §4 — set on a reply, naming
  // the ROOT comment. Replies carry no severity and no disposition: a thread
  // has one disposition, not one per message. Present on the wire from
  // GET /cycles/:id (that route returns whole documents, unprojected —
  // verified 2026-08-20, after a projection gap cost us a day).
  parentComment?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewerContext {
  _id: string;
  reviewer: ReviewerRef;
  domain: 'medical' | 'legal' | 'compliance';
  standingContext: string;
  lastUpdated: string;
}

export interface CycleWithComments {
  cycle: ReviewCycle;
  comments: ReviewComment[];
  /**
   * What THIS user may do on THIS cycle, straight from requireCycleAccess:
   * 'full' for reviewers/DMs/admins, 'participant' for someone who was
   * mentioned or is the named producer.
   *
   * The UI used to infer this from a global permission flag, which is only
   * true for mention-based access — so a producer opening their own cycle got
   * the full composer, and the write was refused server-side (2026-08-21).
   * The server already knows; ask it rather than guessing.
   */
  access?: 'full' | 'participant';
}

export type Severity = ReviewComment['severity'];
export type CycleStatus = ReviewCycle['status'];
export type RiskLevel = NonNullable<ReviewerAssessment['riskLevel']>;
